import React, { useState } from "react";
import { Form, Input, ErrorMsg, Button, Logo, Toast, Select, Badge } from "../../Components";
import { apis, post } from "../../Utils/axios";
import { useNavigate } from "react-router-dom";
import { FiMail } from "react-icons/fi";
import { COMPANY_NAME_MAX_LENGTH, getCompanyNameValidationError, getNameValidationError, getRecaptchaSiteKey, IsDev, limitPhoneDigits, sanitizeCompanyNameInput, validateEmail, validatePhoneNumberDetailed } from "Utils/utils";
import { PHONE_CODE_OPTIONS } from "Utils/constants";
import SubscriptionPlans from "./Subscription";
import { GoogleReCaptcha, GoogleReCaptchaProvider } from "react-google-recaptcha-v3";

const getSignupErrorMessage = (error) => {
	const rawMessage = `${error?.response?.data?.error || error?.message || error || ""}`.trim();
	const normalized = rawMessage.toLowerCase();

	if (!rawMessage) {
		return "We could not complete signup. Please check your details and try again.";
	}
	if (normalized.includes("character varying(10)") || (normalized.includes("phone") && normalized.includes("long"))) {
		return "Phone number is too long. Please enter a valid phone number for the selected country.";
	}
	if ((normalized.includes("email") && normalized.includes("already")) || (normalized.includes("duplicate key") && normalized.includes("email"))) {
		return "This email is already registered. Please log in or use a different email address.";
	}
	if (normalized.includes("email") && normalized.includes("valid")) {
		return "Please enter a valid email address.";
	}
	return rawMessage;
};

function Signup() {
	const NAVIGATE = useNavigate();
	const [UserId, setUserId] = useState();
	const [Values, setValues] = useState({ fName: "", lName: "", email: "", phone: "", company: "", currency: "", phoneCode: {}, country: {} });

	const [ShowPlans, setShowPlans] = useState(false);
	const [CheckInbox, setCheckInbox] = useState(false);
	const [Loader, setLoader] = useState();
	const [Error, setError] = useState();
	const [CaptchaToken, setCaptchaToken] = useState();
	const validateForm = async (e) => {
		e.preventDefault();
		const nameError = getNameValidationError("First name", Values.fName) || getNameValidationError("Last name", Values.lName);
		if (nameError) {
			return Toast.error(nameError);
		}
		const companyError = getCompanyNameValidationError(Values.company);
		if (companyError) {
			return Toast.error(companyError);
		}
		if (!validateEmail(Values.email)) {
			return Toast.error("Please enter a valid email address");
		}
		setError();
		setLoader(true);
		const phoneResult = await validatePhoneNumberDetailed(Values.phone, Values.phoneCode?.value);
		if (!phoneResult.ok) {
			setLoader();
			return Toast.error(phoneResult.message);
		}
		if (!Values.country?.value) {
			setLoader();
			return Toast.error("Please select a country");
		}
		if (!IsDev() && !CaptchaToken) {
			setLoader();
			return Toast.error("Recaptcha verification failed");
		}
		setLoader();
		setShowPlans(true);
	};

	const onSubmit = async (plan) => {
		if (!IsDev() && !CaptchaToken) {
			return Toast.error("Recaptcha verification failed");
		}
		setError();
		setLoader(true);
		post(
			apis.signupUsingEmail,
			{
				...Values,
				phoneCode: Values.phoneCode.value,
				country: Values.country.value,
				currency: Values.country.currency,
				plan,
			},
			(r, e) => {
				setLoader();
				if (r) {
					logRecaptchaEvent((res) => {
						setCheckInbox(true);
						setUserId(r.userId);
					});
				} else if (e) {
					const errorMessage = getSignupErrorMessage(e);
					setError(errorMessage);
					setShowPlans(false);
					Toast.error(errorMessage);
				}
			}
		);
	};

	const logRecaptchaEvent = async (cb) => {
		post(apis.logRecaptchaEvent, { event: "SIGNUP", token: CaptchaToken }, (r, e) => {
			if (r) {
				cb(r);
			} else if (e) {
				console.log(r, e);
			}
		});
	};

	const resendMail = (e) => {
		setError();
		setLoader(true);
		post(apis.resendSignupEmail, { userId: UserId }, (r, e) => {
			if (r) {
				Toast.success("Signup email sent");
				setLoader();
			} else if (e) {
				Toast.handleError(e);
				setLoader();
			}
		});
	};

	if (ShowPlans && !CheckInbox) {
		return <SubscriptionPlans country={Values?.country?.value} error={Error} onCancel={() => setShowPlans(false)} loading={Loader} onSubmit={onSubmit} />;
	}

	return (
		<div className="grid grid-cols-1 md:grid-cols-2 p-4 md:p-6 h-full">
			<div className="relative items-end hidden w-full h-full overflow-hidden text-white bg-center bg-no-repeat bg-cover border-4 md:flex rounded-2xl bg-light border-dark">
				<video muted autoPlay loop className="object-cover w-full h-full ">
					<source src="https://cdn.travelwings.com/assets/corp/videos/cycle.mp4" type="video/mp4" />
					Your browser does not support the video tag.
				</video>
				<div className="absolute left-0 z-10 p-10 h-50" style={{ background: "linear-gradient(to top, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0))" }}>
					<div className="font-bold text-8xl">
						Faster Travel.
						<br />
						Smarter Business.
					</div>
					<div className="mt-4">Simplified expense reimbursements, faster travel bookings, and easy to manage compliances. That`s how we help you to catch your last minute flight. Create a free account, and explore what Travelwings has to offer for your business.</div>
				</div>
			</div>
			<div className="justify-center h-full align-center">
				<div className="w-full max-w-md">
					{CheckInbox ? (
						<>
							<Logo />
							<div className="mt-6 text-5xl font-bold">Check Your Inbox</div>
							<div className="mt-3 text-sm">We just dropped you a mail to verify your email address. Follow the email instructions to activate your account.</div>
							<div className="flex justify-center">
								<Button onClick={() => NAVIGATE("/")} loading={Loader} className="mt-6">
									Go To Log In
								</Button>
								{/* <Button variant="" onClick={resendMail} loading={Loader} className="mt-6">
									Resend Email
								</Button> */}
							</div>
						</>
					) : (
						<>
							<Logo />
							<div className="mt-6 text-5xl font-bold">All Aboard!</div>
							<div className="mt-3 text-sm">Fill in your details to create your new account.</div>
							<div className="flex flex-col mt-10">
								<Form onSubmit={validateForm} loading={Loader} className="space-y-4">
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
										<Input noNumbers value={Values.fName} onChange={(fName) => setValues((prev) => ({ ...prev, fName }))} required label="First Name" placeholder="First Name" />
										<Input noNumbers value={Values.lName} onChange={(lName) => setValues((prev) => ({ ...prev, lName }))} required label="Last Name" placeholder="Last Name" />
									</div>
									<Input type="email" className="mr-2" value={Values.email} onChange={(email) => setValues((prev) => ({ ...prev, email: email.toLowerCase() }))} required label="Email" placeholder="Email" />
									<div>
										<label>Phone Number</label>
										<div className="flex gap-2 mt-2">
											<Select className="max-w-min" placeholder="Code" selected={Values.phoneCode} onChange={(phoneCode) => setValues((prev) => ({ ...prev, phoneCode }))} required label="" options={PHONE_CODE_OPTIONS} />
											<Input className="flex-1" type="number" required value={Values.phone} onChange={(phone) => setValues((prev) => ({ ...prev, phone: limitPhoneDigits(phone) }))} placeholder="Phone" />
										</div>
									</div>
									<Input allowSpecialChars maxLength={COMPANY_NAME_MAX_LENGTH} value={Values.company} onChange={(company) => setValues((prev) => ({ ...prev, company: sanitizeCompanyNameInput(company) }))} required label="Company / Organization" placeholder="Company Name" />
									<Select
										btnClass="w-full"
										selected={Values.country}
										onChange={(country) => setValues((prev) => ({ ...prev, country }))}
										required
										label="Country"
										options={[
											{ label: "United Arab Emirates", value: "AE", currency: "AED" },
											{ label: "South Africa", value: "ZA", currency: "ZAR" },
											{ label: "United States", value: "US", currency: "USD" },
										]}
									/>
									<ErrorMsg e={Error} />
									{!CaptchaToken && !IsDev() && (
										<Badge variant="warning" className="mt-2">
											Verifying Recaptcha...
										</Badge>
									)}
									<Button loading={Loader} type="submit" className="w-full" disabled={!CaptchaToken && !IsDev()}>
										Choose Your Plan
									</Button>
									{!IsDev() && (
										<GoogleReCaptchaProvider reCaptchaKey={getRecaptchaSiteKey()} useEnterprise={true}>
											<GoogleReCaptcha onVerify={setCaptchaToken} />
										</GoogleReCaptchaProvider>
									)}
									<div onClick={() => NAVIGATE("/")} className="mt-4 text-sm text-center cursor-pointer hover:underline">
										Already Have An Account?
									</div>
								</Form>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

export default Signup;
